import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import ModalWrapper from 'Common/Components/ModalWrapper';
import InputString from 'Common/Components/InputString';
import useModalShortcuts from 'Hooks/useModalShortcuts';
import {
  ModalTitle,
  Title,
  ModalContent,
  ButtonsContainer,
  ConfirmButton,
  CancelButton,
  Warning,
  ModalBody,
  RequiredInputWrapper,
  ErrorText
} from './styled';

const MAX_GROUP_NAME = 80;
const normalize = (s) => String(s ?? '').trim();

const EditGroupNameModal = ({
  show,
  currentGroupName,
  existingGroupNames,
  onClose,
  onSave
}) => {
  const [groupName, setGroupName] = useState(currentGroupName || '');
  const [touched, setTouched] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) {
      setIsSubmitting(false);
      return;
    }
    if (isSubmitting) return;

    setGroupName(currentGroupName || '');
    setTouched(false);
    setSubmitError('');
  }, [show, currentGroupName, isSubmitting]);

  const normalizedCurrent = normalize(currentGroupName).toLowerCase();

  const normalizedExistingNames = useMemo(() => {
    return (existingGroupNames || [])
      .map((name) => normalize(name).toLowerCase())
      .filter(Boolean);
  }, [existingGroupNames]);

  const validate = () => {
    const name = normalize(groupName);
    if (!name) return 'Group name is required.';
    if (name.length < 2) return 'Group name must be at least 2 characters.';
    if (name.length > MAX_GROUP_NAME) return `Group name must be at most ${MAX_GROUP_NAME} characters.`;

    const normalizedName = name.toLowerCase();
    const exists = normalizedExistingNames.includes(normalizedName);
    const renamedToSame = normalizedName === normalizedCurrent;

    if (exists && !renamedToSame) return 'This name already exists';

    return '';
  };

  const inlineError = touched && !isSubmitting ? validate() : '';
  const hasError = Boolean(inlineError);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setTouched(true);
    const error = validate();
    if (error) {
      setSubmitError(error);
      return;
    }

    setSubmitError('');
    setIsSubmitting(true);
    let shouldClose = true;

    try {
      if (onSave) {
        const saveResult = await onSave(normalize(groupName));
        shouldClose = saveResult !== null && typeof saveResult !== 'undefined' && saveResult !== false;
      }

      if (shouldClose) {
        onClose();
        return;
      }

      setSubmitError('There was an error, please try again');
    } catch {
      shouldClose = false;
      setSubmitError('There was an error, please try again');
    } finally {
      if (!shouldClose) setIsSubmitting(false);
    }
  };

  useModalShortcuts({
    id: 'vuln-group-name-edit', visible: show, onSave: handleSubmit, onCancel: onClose, canSave: !isSubmitting, enterToSave: true
  });

  if (!show) return null;

  const modalContent = (
    <ModalWrapper>
      <ModalContent>
        <ModalTitle>
          <Warning />
          <Title>Edit Vulnerability Group Name</Title>
        </ModalTitle>

        <ModalBody>
          <RequiredInputWrapper $required>
            <InputString
              title="Group Name"
              placeholder="e.g. Apache Misconfiguration"
              defaultValue={ groupName }
              disabled={ isSubmitting }
              onChange={ (value) => {
                if (isSubmitting) return;
                setGroupName(value);
                if (!touched) setTouched(true);
                setSubmitError('');
              } }
              max={ MAX_GROUP_NAME }
              renderError={ hasError }
              width="100%"
            />
          </RequiredInputWrapper>

          {submitError ? <ErrorText>{submitError}</ErrorText> : null}
        </ModalBody>

        <ButtonsContainer>
          <CancelButton onClick={ onClose } disabled={ isSubmitting }>Cancel</CancelButton>
          <ConfirmButton
            onClick={ handleSubmit }
            disabled={ isSubmitting }
          >
            Save
          </ConfirmButton>
        </ButtonsContainer>
      </ModalContent>
    </ModalWrapper>
  );

  if (typeof document === 'undefined') return modalContent;
  return createPortal(modalContent, document.body);
};

EditGroupNameModal.propTypes = {
  show: PropTypes.bool,
  currentGroupName: PropTypes.string,
  existingGroupNames: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func,
  onSave: PropTypes.func
};

EditGroupNameModal.defaultProps = {
  show: false,
  currentGroupName: '',
  existingGroupNames: [],
  onClose: () => {},
  onSave: null
};

export default EditGroupNameModal;
